import React from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import SEO from '../components/SEO';
import EditorialSubNav from '../components/EditorialSubNav';
import projects from '../data/portfolioProjects';
import { localizeProject } from '../data/portfolioProjects.i18n';
import { useT, useLang } from '../i18n/LanguageContext';

const eyebrowStyle = {
  fontFamily: 'Inter, sans-serif',
  fontSize: '9pt',
  fontWeight: 600,
  letterSpacing: '0.32em',
  textTransform: 'uppercase',
  color: '#D6A87A',
};

const SectionEyebrow = ({ children, color = '#D6A87A' }) => (
  <div style={{ ...eyebrowStyle, color }} className="mb-6">
    {children}
  </div>
);

const ProjectPage = () => {
  const t = useT();
  const { lang } = useLang();
  const { slug } = useParams();
  const baseProject = projects.find((p) => p.slug === slug);

  if (!baseProject) return <Navigate to="/portfolio" replace />;

  const project = localizeProject(baseProject, lang);

  const subNavLinks = [
    { label: t('subnav.brief'), target: 'brief' },
    { label: t('subnav.challenge'), target: 'challenge' },
    { label: t('subnav.intent'), target: 'intent' },
    { label: t('subnav.photos'), target: 'photos' },
    { label: t('subnav.outcome'), target: 'outcome' },
    { label: t('subnav.credits'), target: 'credits' },
  ];

  // Italicise the last word of the title (e.g. "Octapharma EU Congress.") for copper accent.
  const titleParts = (() => {
    const t = project.title.trimEnd();
    const accent = project.titleAccent;
    if (accent && t.endsWith(accent)) {
      const head = t.slice(0, t.length - accent.length).trimEnd();
      return { head, accent };
    }
    return { head: t, accent: '' };
  })();

  return (
    <div data-testid={`project-page-${slug}`}>
      <SEO
        title={`${project.title.replace(/\.$/, '')}, Portfolio | GlowMeets`}
        description={project.sub}
        keywords={`${project.category} case study, ${project.venue}, GlowMeets portfolio`}
        url={`/portfolio/${project.slug}`}
      />

      {/* ─────── Section A, Project hero ─────── */}
      <section
        className="relative w-full overflow-hidden bg-navy"
        style={{ minHeight: '100vh' }}
        data-testid="project-hero"
      >
        <img
          src={project.cover}
          alt={project.title}
          className="absolute inset-0 w-full h-full object-cover"
        />
        {/* Horizontal duotone fade */}
        <div
          className="absolute inset-0"
          style={{
            background:
              'linear-gradient(to right, rgba(10,19,47,0.94) 0%, rgba(10,19,47,0.86) 38%, rgba(10,19,47,0.5) 65%, rgba(10,19,47,0.18) 100%)',
          }}
        />
        {/* Bottom soft fade */}
        <div
          className="absolute inset-x-0 bottom-0"
          style={{
            height: '180px',
            background:
              'linear-gradient(to bottom, rgba(10,19,47,0) 0%, #0A132F 100%)',
          }}
        />

        <div
          className="relative z-10 max-w-container mx-auto px-4 sm:px-6 lg:px-8 flex items-center"
          style={{
            minHeight: '100vh',
            paddingTop: '120px',
            paddingBottom: '90px',
          }}
        >
          <div className="max-w-[58ch]">
            <SectionEyebrow>
              {t(`filters.${project.category.toLowerCase()}`)}, {project.eyebrowDate}
            </SectionEyebrow>

            <h1
              className="text-bone"
              style={{
                fontFamily: '"Cormorant Garamond", serif',
                fontSize: 'clamp(38px, 4.8vw, 64pt)',
                lineHeight: 1.05,
                fontWeight: 500,
                letterSpacing: '-0.005em',
              }}
            >
              {titleParts.head}{' '}
              <em
                className="italic"
                style={{ color: '#D6A87A', fontWeight: 400 }}
              >
                {titleParts.accent}
              </em>
            </h1>

            <p
              className="mt-8"
              style={{
                fontFamily: '"Cormorant Garamond", serif',
                fontStyle: 'italic',
                fontWeight: 400,
                fontSize: '18pt',
                color: 'rgba(244,239,227,0.82)',
                lineHeight: 1.5,
                maxWidth: '60ch',
              }}
            >
              {project.sub}
            </p>

            <div
              className="mt-10"
              style={{
                height: '1px',
                width: '88px',
                backgroundColor: 'rgba(214,168,122,0.55)',
              }}
            />
          </div>
        </div>
      </section>

      {/* Sticky in-page sub-nav */}
      <EditorialSubNav links={subNavLinks} surface="bone" />

      {/* ─────── Section B, The brief ─────── */}
      <section
        id="brief"
        className="bg-bone text-ink"
        style={{ paddingTop: '90px', paddingBottom: '90px' }}
        data-testid="project-brief"
      >
        <div className="max-w-container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-[70ch]">
            <SectionEyebrow>{t('project.briefEyebrow')}</SectionEyebrow>
            <h2
              style={{
                fontFamily: '"Cormorant Garamond", serif',
                fontStyle: 'italic',
                fontSize: 'clamp(24px, 2.4vw, 32pt)',
                lineHeight: 1.2,
                fontWeight: 500,
                color: '#0A132F',
                letterSpacing: '-0.005em',
              }}
            >
              {project.briefHeadline || t('project.briefHeadline')}
            </h2>
            <p
              className="mt-7"
              style={{
                fontFamily: 'Inter, sans-serif',
                fontSize: '13pt',
                fontWeight: 400,
                lineHeight: 1.7,
                color: '#4A4F63',
              }}
            >
              {project.briefBody}
            </p>
          </div>
        </div>
      </section>

      {/* ─────── Section C, The challenge ─────── */}
      <section
        id="challenge"
        className="bg-bone text-ink"
        style={{ paddingTop: '0', paddingBottom: '90px' }}
        data-testid="project-challenge"
      >
        <div className="max-w-container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-[70ch]">
            <SectionEyebrow>{t('project.challengeEyebrow')}</SectionEyebrow>
            <p
              className="mt-2"
              style={{
                fontFamily: 'Inter, sans-serif',
                fontSize: '13pt',
                fontWeight: 400,
                lineHeight: 1.7,
                color: '#4A4F63',
              }}
            >
              {project.challenge}
            </p>
            <div
              className="mt-10"
              style={{
                height: '1px',
                width: '64px',
                backgroundColor: 'rgba(214,168,122,0.55)',
              }}
            />
          </div>
        </div>
      </section>

      {/* ─────── Section D, The design intent (graphite navy) ─────── */}
      <section
        id="intent"
        className="text-bone w-full"
        style={{
          paddingTop: '100px',
          paddingBottom: '100px',
          background:
            'linear-gradient(180deg, #0E1B3F 0%, #0A1130 50%, #060B22 100%)',
        }}
        data-testid="project-intent"
      >
        <div className="max-w-container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-[70ch]">
            <SectionEyebrow>{t('project.intentEyebrow')}</SectionEyebrow>
            <h2
              style={{
                fontFamily: '"Cormorant Garamond", serif',
                fontSize: 'clamp(24px, 2.4vw, 32pt)',
                lineHeight: 1.2,
                fontWeight: 500,
                color: '#F4EFE3',
                letterSpacing: '-0.005em',
              }}
            >
              {project.intentHeadline || (
                <>
                  {t('project.intentHead')}{' '}
                  <em
                    className="italic"
                    style={{ color: '#D6A87A', fontWeight: 400 }}
                  >
                    {t('project.intentAccent')}
                  </em>
                </>
              )}
            </h2>
            <p
              className="mt-7"
              style={{
                fontFamily: 'Inter, sans-serif',
                fontSize: '13pt',
                fontWeight: 400,
                lineHeight: 1.7,
                color: 'rgba(244,239,227,0.82)',
              }}
            >
              {project.intent}
            </p>
          </div>
        </div>
      </section>

      {/* ─────── Section E, Photo essay ─────── */}
      <section
        id="photos"
        className="bg-white"
        style={{ paddingTop: '90px', paddingBottom: '90px' }}
        data-testid="project-photos"
      >
        <div className="w-full">
          {project.essay.map((photo, i) => (
            <figure
              key={`${slug}-photo-${i}`}
              className="mx-auto mb-8"
              style={{ width: '70vw', maxWidth: '1400px' }}
              data-testid={`project-photo-${i + 1}`}
            >
              <img
                src={photo.src}
                alt={photo.caption}
                className="w-full h-auto"
                style={{ display: 'block' }}
              />
              <figcaption
                className="mt-4 text-center mx-auto"
                style={{
                  fontFamily: '"Cormorant Garamond", serif',
                  fontStyle: 'italic',
                  fontWeight: 400,
                  fontSize: '11pt',
                  color: '#4A4F63',
                  lineHeight: 1.55,
                  maxWidth: '60ch',
                }}
              >
                {photo.caption}
              </figcaption>
            </figure>
          ))}
        </div>
      </section>

      {/* ─────── Section F, The outcome (navy band) ─────── */}
      <section
        id="outcome"
        className="bg-navy text-bone w-full"
        style={{ paddingTop: '90px', paddingBottom: '90px' }}
        data-testid="project-outcome"
      >
        <div className="max-w-container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <SectionEyebrow>{t('project.outcomeEyebrow')}</SectionEyebrow>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-y-10 md:gap-y-0">
            {project.numbers.map((n, i) => (
              <div
                key={n.label}
                className="text-center md:px-8"
                style={{
                  borderRight:
                    i < project.numbers.length - 1
                      ? '1px solid rgba(214,168,122,0.35)'
                      : 'none',
                }}
                data-testid={`outcome-${i + 1}`}
              >
                <div
                  style={{
                    fontFamily: '"Cormorant Garamond", serif',
                    fontSize: '56pt',
                    lineHeight: 1,
                    fontWeight: 500,
                    color: '#D6A87A',
                    letterSpacing: '-0.005em',
                  }}
                >
                  {n.value}
                </div>
                <div
                  className="mt-4"
                  style={{
                    fontFamily: 'Inter, sans-serif',
                    fontSize: '9pt',
                    fontWeight: 600,
                    letterSpacing: '0.32em',
                    textTransform: 'uppercase',
                    color: 'rgba(214,168,122,0.85)',
                  }}
                >
                  {n.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─────── Section G, Credits ─────── */}
      <section
        id="credits"
        className="bg-bone text-ink"
        style={{ paddingTop: '90px', paddingBottom: '90px' }}
        data-testid="project-credits"
      >
        <div className="max-w-container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-[70ch]">
            <SectionEyebrow>{t('project.creditsEyebrow')}</SectionEyebrow>

            <dl className="space-y-4 mt-2">
              {Object.entries(project.credits).map(([label, value]) => (
                <div
                  key={label}
                  className="grid grid-cols-1 sm:grid-cols-12 gap-y-1 gap-x-6 items-baseline"
                  style={{
                    paddingBottom: '14px',
                    borderBottom: '1px solid rgba(214,168,122,0.25)',
                  }}
                >
                  <dt
                    className="sm:col-span-3"
                    style={{
                      fontFamily: 'Inter, sans-serif',
                      fontSize: '9pt',
                      fontWeight: 600,
                      letterSpacing: '0.22em',
                      textTransform: 'uppercase',
                      color: '#D6A87A',
                    }}
                  >
                    {t(`project.creditLabels.${label.toLowerCase()}`)}
                  </dt>
                  <dd
                    className="sm:col-span-9"
                    style={{
                      fontFamily: 'Inter, sans-serif',
                      fontSize: '12pt',
                      fontWeight: 400,
                      color: '#0A132F',
                    }}
                  >, {value}
                  </dd>
                </div>
              ))}
            </dl>
          </div>
        </div>
      </section>

      {/* ─────── Section H, Closing band ─────── */}
      <section
        className="bg-navy text-bone w-full text-center"
        style={{ paddingTop: '90px', paddingBottom: '90px' }}
        data-testid="project-closing"
      >
        <div className="max-w-container mx-auto px-4 sm:px-6 lg:px-8 flex flex-col items-center">
          <img
            src="/glowmeets-mark.png"
            alt="GlowMeets compass mark"
            style={{ height: '56px', width: 'auto' }}
          />

          <p
            className="mt-6"
            style={{
              fontFamily: '"Cormorant Garamond", serif',
              fontStyle: 'italic',
              fontWeight: 400,
              fontSize: '14pt',
              color: 'rgba(244,239,227,0.7)',
              lineHeight: 1.5,
            }}
          >
            {t('project.closingLine')}
          </p>

          <div className="mt-8 flex flex-col md:flex-row items-center justify-center gap-4">
            <Link
              to="/portfolio"
              data-testid="closing-read-another"
              className="inline-flex items-center justify-center transition-colors"
              style={{
                fontFamily: 'Inter, sans-serif',
                fontSize: '11pt',
                fontWeight: 600,
                letterSpacing: '0.18em',
                textTransform: 'uppercase',
                color: '#D6A87A',
                backgroundColor: 'transparent',
                border: '1px solid #D6A87A',
                padding: '0 22px',
                borderRadius: '999px',
                minWidth: '220px',
                height: '48px',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#D6A87A';
                e.currentTarget.style.color = '#0A132F';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.color = '#D6A87A';
              }}
            >
              {t('cta.readAnother')}
            </Link>

            <Link
              to={project.chapterRoute}
              data-testid="closing-view-chapter"
              className="inline-flex items-center justify-center transition-colors"
              style={{
                fontFamily: 'Inter, sans-serif',
                fontSize: '11pt',
                fontWeight: 600,
                letterSpacing: '0.18em',
                textTransform: 'uppercase',
                color: '#D6A87A',
                backgroundColor: 'transparent',
                border: '1px solid #D6A87A',
                padding: '0 22px',
                borderRadius: '999px',
                minWidth: '220px',
                height: '48px',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#D6A87A';
                e.currentTarget.style.color = '#0A132F';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.color = '#D6A87A';
              }}
            >
              {t('cta.viewChapter', { category: t(`filters.${project.category.toLowerCase()}`) })}
            </Link>

            <Link
              to="/contact"
              data-testid="closing-book"
              className="inline-flex items-center justify-center transition-colors"
              style={{
                fontFamily: 'Inter, sans-serif',
                fontSize: '11pt',
                fontWeight: 600,
                letterSpacing: '0.18em',
                textTransform: 'uppercase',
                color: '#D6A87A',
                backgroundColor: 'transparent',
                border: '1px solid #D6A87A',
                padding: '0 22px',
                borderRadius: '999px',
                minWidth: '220px',
                height: '48px',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#D6A87A';
                e.currentTarget.style.color = '#0A132F';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.color = '#D6A87A';
              }}
            >
              {t('cta.bookConsultation')} →
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ProjectPage;
